import {
  Box,
  Button,
  Text,
} from '@chakra-ui/react';
import { CopyIcon } from '@chakra-ui/icons';
import { Card } from './Card';
import type League from '@/models/league';
import type { leagueMember } from '@/models/user';
import {
  getCompetingCount,
  getListPosition,
  isPendingLeagueRank,
  isSettledLeagueRank,
} from '@/helpers/leagues/leagueMemberSync';
import { FirebaseContext } from '@/session/FirebaseContext';
import { useContext } from 'react';
import { ensureLeaderboardCache } from '@/helpers/leagues/leagueLeaderboardCache';
import { updateLeague } from '@/models/league';
import { formatLeagueStandingsCopyText } from '@/helpers/leagues/leagueStandingsCopy';
import { httpsCallable } from 'firebase/functions';
import { pageButtonProps, pageSectionTitleProps } from '@/theme/sitePageTypography';
import { LeaderboardList } from './LeaderboardList';
import type { LeaderboardMemberRow } from './LeaderboardRow';
import { LeaderboardPageControls } from './LeaderboardPageControls';
import { useLeaderboardPages } from '@/hooks/useLeaderboardPages';

interface LeaderboardCardProps {
  league: League & { id: string };
  isAdmin: boolean;
  isGlobalLeague: boolean;
  tournamentStarted: boolean;
  copyStandingsAsync: (getText: () => Promise<string>) => Promise<void>;
  onViewMemberList?: (uid: string, member: leagueMember) => void;
  /** When true, omit outer card chrome (content sits inside a profile-style card). */
  embedded?: boolean;
}

export function LeaderboardCard({
  league,
  isAdmin,
  isGlobalLeague,
  tournamentStarted,
  copyStandingsAsync,
  onViewMemberList,
  embedded = false,
}: LeaderboardCardProps) {
  const ctx = useContext(FirebaseContext);

  const {
    pageIndex,
    loading,
    members,
    memberIds,
    membersLoaded,
    membersPerPage,
    totalPages,
    canGoFirst,
    canGoPrev,
    canGoNext,
    canGoLast,
    goToFirstPage,
    goToPrevPage,
    goToNextPage,
    goToLastPage,
  } = useLeaderboardPages(league);

  const competingCount = getCompetingCount(league);

  async function transferOwnership(newAdminUID: string) {
    await updateLeague(ctx.db, league.id, { adminUID: newAdminUID });
  }

  async function kickMember(memberUID: string) {
    await httpsCallable(ctx.funcs, 'leaveLeague')({ leagueUID: league.id, kickMemberUID: memberUID });
  }

  function copyFullLeagueStandings(): void {
    void copyStandingsAsync(async () => {
      const entry = await ensureLeaderboardCache(ctx.db, league);
      return formatLeagueStandingsCopyText(
        league,
        entry.membersByUid,
        entry.memberIdsInOrder,
        { tournamentStarted },
      );
    });
  }

  const paginationControls = (placement: 'top' | 'bottom') =>
    competingCount > membersPerPage ? (
      <LeaderboardPageControls
        pageIndex={pageIndex}
        totalPages={totalPages}
        canGoFirst={canGoFirst}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
        canGoLast={canGoLast}
        onFirst={goToFirstPage}
        onPrev={goToPrevPage}
        onNext={goToNextPage}
        onLast={goToLastPage}
        placement={placement}
      />
    ) : null;

  const rowDataList: LeaderboardMemberRow[] | null =
    members && membersLoaded
      ? memberIds.map((x, i) => ({
          memberUid: x,
          member: members[x],
          displayRank: isSettledLeagueRank(members[x].currentRank)
            ? members[x].currentRank
            : isPendingLeagueRank(members[x].currentRank)
              ? 0
              : membersPerPage * pageIndex + i + 1,
          isYou: x === ctx.user!.uid,
          isLeagueAdmin: x === league.adminUID,
          showRankChange: isSettledLeagueRank(members[x].currentRank),
          listPosition: getListPosition(league, members[x], x),
          isAdminViewer: isAdmin,
          canKick: x !== league.adminUID,
          tournamentStarted,
        }))
      : null;

  const body = (
    <>
      <Text {...pageSectionTitleProps} mb={2}>
        Leaderboard
      </Text>
      {paginationControls('top')}
      <Box display='flex' flexDirection='column'>
        <LeaderboardList
          rows={rowDataList}
          isInitialLoading={!membersLoaded}
          isPageLoading={loading && membersLoaded}
          onTransferOwnership={(uid) => void transferOwnership(uid)}
          onKick={(uid) => void kickMember(uid)}
          onViewMemberList={onViewMemberList}
        />
      </Box>
      {paginationControls('bottom')}
      {!isGlobalLeague ? (
        <Button
          {...pageButtonProps}
          mt={3}
          variant='outline'
          alignSelf={{ base: 'stretch', sm: 'center' }}
          w={{ base: 'full', sm: 'auto' }}
          rightIcon={<CopyIcon boxSize={3} />}
          onClick={copyFullLeagueStandings}
        >
          Copy standings
        </Button>
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <Box display='flex' flexDirection='column' overflow='visible' w='full' h='full'>
        {body}
      </Box>
    );
  }

  return (
    <Card display='flex' flexDirection='column' overflow='visible'>
      {body}
    </Card>
  );
}
